import { Alert, Button, Card, Input, Space, Tag, Typography, message } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { ZhMap, type ZhMapRef } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import type { GeoPoint } from "@zhihaoscm/entity";

export interface RecommendedShip {
    id: string | number;
    status?: number;
    acceptStatus?: boolean;
    ship?: { id?: string | number; name?: string; cnname?: string; tonCapacity?: number; mmsi?: string };
}

export interface PlatformShipInviteMapPageProps {
    loadShips?: (id: string) => Promise<RecommendedShip[]>;
    loadShipsByArea?: (params: PlatformShipInviteAreaQuery) => Promise<RecommendedShip[]>;
    inviteShip?: (platId: string, shipIds: Array<string | number>) => Promise<unknown>;
}

export interface PlatformShipInviteAreaQuery {
    platId: string;
    geoPointList: GeoPoint[];
    time: number;
}

// 沿用 Vue 邀请地图的稳定默认视口，仅接入地图壳，不触发区域绘制或点位业务。
const INVITE_MAP_CENTER: [number, number] = [114.84, 30.52];
const INVITE_MAP_ZOOM = 13;

/** 复用 Vue 智能推荐船舶接口。地图底图由模板能力承载，列表与邀请链先保持完整。 */
export async function loadPlatformRecommendedShips(id: string): Promise<RecommendedShip[]> {
    const response = await shippingRequestHttp.service.get<RecommendedShip[]>(`/shipping/plat/find/smart/${id}`, {
        params: {}
    });
    return response.data || [];
}

/** 复用 Vue 平台邀请船舶接口。 */
export async function invitePlatformShips(platId: string, shipIds: Array<string | number>) {
    const response = await shippingRequestHttp.service.put("/shipping/plat/invite", { platId, shipIds });
    return response.data;
}

/** 自定义区域推荐接口沿用 Vue extentInvite 的 geoPointList/time 参数。 */
export async function loadPlatformShipsByArea(params: PlatformShipInviteAreaQuery): Promise<RecommendedShip[]> {
    const response = await shippingRequestHttp.service.post<RecommendedShip[]>("/shipping/plat/extent/invite", params);
    return response.data || [];
}

/** 平台船运需求自定义邀请页，保留 Vue 路由、筛选、单邀和一键邀请入口。 */
export function PlatformShipInviteMapPage({
    loadShips = loadPlatformRecommendedShips,
    loadShipsByArea = loadPlatformShipsByArea,
    inviteShip = invitePlatformShips
}: PlatformShipInviteMapPageProps) {
    const { id: routeId } = useParams();
    const location = useLocation();
    const id = routeId || location.pathname.split("/").filter(Boolean).pop() || "";
    const navigate = useNavigate();
    const [ships, setShips] = useState<RecommendedShip[]>([]);
    const [selectedIds, setSelectedIds] = useState<Array<string | number>>([]);
    const [keyword, setKeyword] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [drawn, setDrawn] = useState(false);
    const [areaLoading, setAreaLoading] = useState(false);
    const mapRef = useRef<ZhMapRef | null>(null);

    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void loadShips(id)
            .then(data => {
                if (!disposed) setShips(data);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("推荐船舶加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadShips]);

    // 进入邀船页立即开启区域绘制，离开时关闭并清理绘制状态。
    useEffect(() => {
        mapRef.current?.drawPolygon.open();
        return () => {
            mapRef.current?.drawPolygon.close();
            mapRef.current?.drawPolygon.reset();
        };
    }, []);

    /** 地图移动时保留 Vue 的视口范围计算，并按缩放级别切换绿点图层。 */
    const onMapMoveEnd = (zoom: number) => {
        const extent = mapRef.current?.utils.getCalculateExtent();
        if (!extent) return;
        mapRef.current?.layer.setGreenTileVisible(zoom < 10);
    };

    /** 区域绘制完成后转换坐标并调用 Vue extentInvite 接口。 */
    const onAreaDrawEnd = async (area: number[][]) => {
        if (!id || !area.length || areaLoading) return;
        const geoPointList = area.map(([lng, lat]) => ({ lng, lat }));
        setAreaLoading(true);
        setErrorMessage("");
        try {
            const nextShips = await loadShipsByArea({
                platId: id,
                geoPointList,
                time: Date.now() - 10 * 60 * 1000
            });
            setShips(nextShips || []);
            setDrawn(true);
        } catch {
            setErrorMessage("区域船舶加载失败");
        } finally {
            setAreaLoading(false);
        }
    };

    const visibleShips = useMemo(() => {
        const query = keyword.trim().toLowerCase();
        if (!query) return ships;
        return ships.filter(item =>
            [item.ship?.name, item.ship?.cnname, item.ship?.mmsi, item.id]
                .filter(Boolean)
                .some(value => String(value).toLowerCase().includes(query))
        );
    }, [keyword, ships]);

    const invite = async (shipIds: Array<string | number>) => {
        if (!shipIds.length) {
            message.warning("暂无可以邀请的船舶");
            return;
        }
        try {
            await inviteShip(id, shipIds);
            message.success("已邀请");
            setShips(current =>
                current.map(item => (shipIds.includes(item.id) ? { ...item, acceptStatus: true } : item))
            );
        } catch {
            setErrorMessage("船舶邀请失败");
        }
    };

    return (
        <section aria-label="platform-ship-invite-map-page" className="flex min-h-full flex-col gap-4">
            <div aria-label="船舶邀请地图" className="min-h-[360px] overflow-hidden rounded-md">
                <ZhMap
                    center={INVITE_MAP_CENTER}
                    className="h-full min-h-[360px] w-full"
                    layerType="satellite"
                    ref={mapRef}
                    scaleLineUnit="nautical"
                    mapMoveEnd={onMapMoveEnd}
                    areaDrawEnd={onAreaDrawEnd}
                    showGreenLayer
                    showScale
                    zoom={INVITE_MAP_ZOOM}
                />
            </div>
            <span aria-label="邀船区域已绘制" className="sr-only">
                {drawn ? "已绘制" : "未绘制"}
            </span>
            <Card>
                <Space align="center" wrap>
                    <Typography.Title className="mb-0" level={4}>
                        自定义邀请
                    </Typography.Title>
                    <Tag>船运需求 {id}</Tag>
                    <span>地图框选区域后推荐船舶</span>
                </Space>
            </Card>
            <Card title="推荐船舶">
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Space className="mb-4" wrap>
                    <Input
                        aria-label="船舶搜索"
                        placeholder="输入船舶名称或 MMSI 搜索"
                        value={keyword}
                        onChange={event => setKeyword(event.target.value)}
                    />
                    <Button
                        aria-label="一键发短信"
                        type="primary"
                        loading={areaLoading}
                        onClick={() =>
                            void invite(
                                selectedIds.length
                                    ? selectedIds
                                    : visibleShips.filter(item => !item.acceptStatus).map(item => item.id)
                            )
                        }
                    >
                        一键发短信
                    </Button>
                </Space>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    {visibleShips.map(item => {
                        const shipName = item.ship?.name || item.ship?.cnname || item.id;
                        const selected = selectedIds.includes(item.id);
                        return (
                            <Card key={item.id} size="small" title={shipName}>
                                <Space direction="vertical">
                                    <span>MMSI：{item.ship?.mmsi || "-"}</span>
                                    <span>载重：{item.ship?.tonCapacity ?? "-"} 吨</span>
                                    <Space>
                                        <Button
                                            aria-label={`选择${shipName}`}
                                            onClick={() =>
                                                setSelectedIds(current =>
                                                    selected
                                                        ? current.filter(idValue => idValue !== item.id)
                                                        : [...current, item.id]
                                                )
                                            }
                                        >
                                            {selected ? "取消选择" : "选择"}
                                        </Button>
                                        <Button
                                            aria-label={item.acceptStatus ? "平台当天已邀请" : "邀请报价"}
                                            disabled={item.acceptStatus}
                                            onClick={() => void invite([item.id])}
                                        >
                                            {item.acceptStatus ? "平台当天已邀请" : "邀请报价"}
                                        </Button>
                                    </Space>
                                </Space>
                            </Card>
                        );
                    })}
                </div>
                {!visibleShips.length ? (
                    <Typography.Text type="secondary">暂无数据，请在地图中圈选区域</Typography.Text>
                ) : null}
                <Button className="mt-6" aria-label="返回" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </Card>
        </section>
    );
}

export default PlatformShipInviteMapPage;
